import type { Direction, Modality } from '../contracts';
import { nextProgressionLevel, stakeForLevel, type ProgressionConfig } from '../progression';
import { ROUND_CLOCKS, roundKind } from '../rounds';
import { roundOutcome, type BacktestRound } from './backtest';
import { customStrategy, CUSTOM_LIMITS, type CustomState, type CustomStrategyDef } from './custom';
import type { MarketTick } from './types';

/**
 * "Melhor sequência": every Entrada/Oposto gale plan of a given length replayed over past rounds, the way
 * backtestStrategy replays one (same reading, entries, ties and gales), ranked by red cycles and then by result.
 * Owner's request 2026-10-29: picking the sides gale by gale by hand lost cycles a plan next to it would have won.
 *
 * All plans run side by side in one pass over the rounds: plans in the same situation (gale level and robot state)
 * share one reading, and split only when their next gale goes to different sides. Between cycles they are almost
 * always back together, so 256 plans cost a few single replays. Past results never promise future ones.
 */

export type GaleStep = 'ENTRY' | 'OPPOSITE';

export interface GalePlanResult {
  plan: GaleStep[];
  chains: number;
  chainWins: number;
  /** Cycles lost on the last gale. */
  reds: number;
  net: number;
  maxDrawdown: number;
}

export interface GaleSearchInput {
  def: CustomStrategyDef;
  modality: Modality;
  /** Oldest first, consecutive rounds of one clock. */
  rounds: readonly BacktestRound[];
  /** Gales in the plan (1–CUSTOM_LIMITS.galePlan): every one of the 2^gales plans is tried. */
  gales: number;
  baseStake: number;
  multiplier: string;
  payoutRate: number;
  windowSize?: number;
}

/** Plan number n → steps, first gale first (bit set = Oposto). */
export function galePlanOf(n: number, gales: number): GaleStep[] {
  return Array.from({ length: gales }, (_, k) => ((n >> (gales - 1 - k)) & 1 ? 'OPPOSITE' : 'ENTRY'));
}

/** Fewer reds first, then the higher result, then fewer switches of side (the simpler plan). */
export function compareGalePlans(a: GalePlanResult, b: GalePlanResult): number {
  const switches = (p: GaleStep[]) => p.filter((x) => x === 'OPPOSITE').length;
  return a.reds - b.reds || b.net - a.net || switches(a.plan) - switches(b.plan);
}

interface Group {
  /** Plan numbers in this situation. */
  plans: number[];
  level: number;
  state: CustomState;
}

/** Every plan of `gales` steps, best first. */
export function searchGalePlans(input: GaleSearchInput): GalePlanResult[] {
  const { modality, rounds, baseStake, payoutRate } = input;
  const gales = Math.max(1, Math.min(CUSTOM_LIMITS.galePlan, Math.floor(input.gales)));
  const total = 2 ** gales;
  const windowSize = input.windowSize ?? 160;
  const progression: ProgressionConfig = { enabled: true, multiplier: input.multiplier, maxLevels: gales, onMaxLevel: 'RESET' };
  const stakes = Array.from({ length: gales + 1 }, (_, l) => Number(stakeForLevel(baseStake, progression, l)));
  const steps = Array.from({ length: total }, (_, n) => galePlanOf(n, gales));
  // Each plan's own definition (the gale sides are all that differs).
  const defs = steps.map((plan) => ({ ...input.def, gale: { direction: 'SAME' as const, plan } }));
  const { stops } = input.def;
  // Win/loss counters only matter to the stops: without them, plans that differ only there are in the same situation.
  const keyOf = (g: Group) =>
    `${g.level}|${JSON.stringify({ ...g.state, note: '', wins: stops.wins > 0 ? g.state.wins : 0, lossStreak: stops.lossStreak > 0 ? g.state.lossStreak : 0 })}`;

  const acc = steps.map((plan) => ({ plan, chains: 0, chainWins: 0, reds: 0, net: 0, peak: 0, maxDrawdown: 0 }));
  let groups: Group[] = [{ plans: steps.map((_, n) => n), level: 0, state: customStrategy.initialState(input.def, modality) as CustomState }];

  const cycle = ROUND_CLOCKS[roundKind(modality)].cycleSeconds;
  const N = rounds.length;
  const revDigits = rounds.map((x) => x.digit).reverse();
  const revMoves = rounds.map((x) => x.move ?? 'FLAT').reverse();
  const revIds = rounds.map((x) => x.id).reverse();

  for (let i = 1; i < N && groups.length; i++) {
    const r = rounds[i]!;
    const from = Math.max(0, i - windowSize);
    const window = { digits: revDigits.slice(N - i, N - from), quotes: [], moves: revMoves.slice(N - i, N - from), roundIds: revIds.slice(N - i, N - from) };
    const tick: MarketTick = { symbol: '', epoch: r.endAt - cycle, quote: 0, pipSize: 2, digit: rounds[i - 1]!.digit };
    const next: Group[] = [];

    for (const g of groups) {
      // In a gale, the plans of this group go to the side of their own step: one reading per side.
      const parts =
        g.level > 0 && g.level <= gales
          ? [g.plans.filter((n) => steps[n]![g.level - 1] === 'ENTRY'), g.plans.filter((n) => steps[n]![g.level - 1] === 'OPPOSITE')].filter((p) => p.length)
          : [g.plans];
      for (const plans of parts) {
        const def = defs[plans[0]!]!;
        const ev = customStrategy.onTick({ modality, params: def, state: g.state, window, level: g.level, roundId: r.id }, tick);
        if (!ev.intent) {
          next.push({ plans, level: g.level, state: ev.state as CustomState });
          continue;
        }
        const direction = ev.intent.direction as Direction;
        const stake = stakes[g.level]!;
        const outcome = roundOutcome(modality, direction, r);
        const profit = outcome === 'WIN' ? Math.round(stake * payoutRate * 100) / 100 : outcome === 'LOSS' ? -stake : 0;
        const step = nextProgressionLevel(g.level, outcome, progression);
        const chainOver = step.level === 0 || step.stop;
        for (const n of plans) {
          const a = acc[n]!;
          a.net = Math.round((a.net + profit) * 100) / 100;
          a.peak = Math.max(a.peak, a.net);
          a.maxDrawdown = Math.max(a.maxDrawdown, Math.round((a.peak - a.net) * 100) / 100);
          if (g.level === 0 && outcome !== 'NEUTRAL') a.chains++;
          if (chainOver && outcome === 'WIN') a.chainWins++;
          else if (chainOver && outcome === 'LOSS') a.reds++;
        }
        const state = customStrategy.onSettlement({ modality, params: def, state: ev.state, window }, { direction, outcome, chainOver }) as CustomState;
        // A stop of the robot itself ends these plans' run, as it ends the replay.
        if (customStrategy.shouldEnd?.(state, def)) continue;
        next.push({ plans, level: step.stop ? 0 : step.level, state });
      }
    }

    // Plans that ended up in the same situation read the next round together again.
    const merged = new Map<string, Group>();
    for (const g of next) {
      const k = keyOf(g);
      const m = merged.get(k);
      if (m) m.plans.push(...g.plans);
      else merged.set(k, { ...g, plans: [...g.plans] });
    }
    groups = [...merged.values()];
  }

  return acc.map(({ peak: _, ...a }) => a).sort(compareGalePlans);
}
